import React, { useState, useEffect, useCallback } from 'react';
import { Button, Col, message, Row, Select, Table, Spin, Empty, Tag, Descriptions } from 'antd';
import { EditOutlined, DeleteOutlined, CalendarOutlined, LockOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';
import { request } from '../../../../shared/src/apis';
import { getPaymentCyclesApi } from '../../utils/apisConsts';
import { errorMessage } from '../../utils/commonStringsConsts';
import helper from '../../utils/helper';
import { ICollection } from '../../utils/commonInterfaces';
import EditCollectionModal from '../../components/editCollectionModal';
import DeleteCollectionModal from '../../components/deleteCollectionModal';
import { getCollectionsApi, updateOrDeleteCollectionApi } from '../../utils/apisConsts';

const { Option } = Select;

const EditAndDelete: React.FC = () => {
    const dispatch = useDispatch();
    const info = useSelector((state: any) => state.actionsReducer.info);
    const [paymentCycles, setPaymentCycles] = useState<any[]>([]);
    const [loadingPaymentCycles, setLoadingPaymentCycles] = useState<boolean>(false);
    const [selectedPaymentCycle, setSelectedPaymentCycle] = useState<number | null>(null);
    const [invoiceSummary, setInvoiceSummary] = useState<any>({});
    const [collections, setCollections] = useState<ICollection[]>([]);
    const [isEditModalVisible, setIsEditModalVisible] = useState<boolean>(false);
    const [selectedCollection, setSelectedCollection] = useState<ICollection | null>(null);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
    const [collectionToDelete, setCollectionToDelete] = useState<ICollection | null>(null);
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
    const [loadingCollections, setLoadingCollections] = useState<boolean>(false);
    const [updatingCollection, setUpdatingCollection] = useState<boolean>(false);
    const [paginationConfig, setPaginationConfig] = useState({
        current: 1,
        pageSize: 10,
    });

    const getPaymentCycles = useCallback(async () => {
        if (!info?.id) {
            return;
        }

        setLoadingPaymentCycles(true);
        try {
            const paymentCyclesRes: any = await dispatch(request({
                url: getPaymentCyclesApi,
                method: 'GET',
                params: {
                    companyID: info?.id
                }
            }));

            if (paymentCyclesRes?.data?.status === 200) {
                setPaymentCycles(paymentCyclesRes?.data?.allPaymentCycles?.reverse() || []);
            } else {
                message.error(paymentCyclesRes?.data?.message || errorMessage, 4);
            }
        } catch (error: any) {
            message.error(errorMessage, 4);
        } finally {
            setLoadingPaymentCycles(false);
        }
    }, [info?.id, dispatch]);

    const getCollections = async (paymentCycle: number): Promise<ICollection[]> => {
        setLoadingCollections(true);
        try {
            const res: any = await dispatch(request({
                url: getCollectionsApi,
                method: 'GET',
                params: {
                    companyID: info.id,
                    paymentCycle: paymentCycle,
                }
            }));
            if (res?.data?.status === 200) {
                setInvoiceSummary(res?.data?.data?.invoiceSummary || {});
                return res?.data?.data?.collections as ICollection[] || [];
            } else {
                message.error(res?.data?.message || errorMessage, 4);
                return [];
            }
        }
        catch (error: any) {
            message.error(error?.response?.data?.status === 400 ? error?.response?.data?.message : errorMessage, 4);
            return [];
        } finally {
            setLoadingCollections(false);
        }
    };

    useEffect(() => {
        if (info?.id) {
            getPaymentCycles();
        } else {
            setPaymentCycles([]);
            setSelectedPaymentCycle(null);
            setCollections([]);
        }
    }, [info?.id, getPaymentCycles]);

    const handlePaymentCycleChange = async (paymentCycle: number) => {
        setSelectedPaymentCycle(paymentCycle);
        setPaginationConfig({
            current: 1,
            pageSize: 10,
        });
        const Collections = await getCollections(paymentCycle);
        setCollections(Collections);
    };

    const handleEditCollection = (record: ICollection) => {
        setSelectedCollection(record);
        setIsEditModalVisible(true);
    };

    const handleCloseEditModal = () => {
        setIsEditModalVisible(false);
        setSelectedCollection(null);
    };

    const handleUpdateCollection = async (values: any) => {
        setUpdatingCollection(true);
        try {
            const res: any = await dispatch(request({
                url: updateOrDeleteCollectionApi,
                method: 'PATCH',
                data: {
                    collectionDate: values.collectionDate,
                    amount: values.amount,
                    companyID: info.id,
                    paymentCycle: selectedPaymentCycle,
                    id: values.id,
                    fullyPaid: values.fullyPaid,
                }
            }));
            if (res?.data?.status === 200) {
                message.success(res?.data?.message);
                if (selectedPaymentCycle !== null) {
                    setCollections(res?.data?.data?.collections);
                    setInvoiceSummary(res?.data?.data?.invoiceSummary);
                }
                handleCloseEditModal();
            } else {
                message.error(res?.data?.message || errorMessage, 4);
                handleCloseEditModal();
            }
        } catch (error: any) {
            message.error(errorMessage, 4);
            handleCloseEditModal();
        } finally {
            setUpdatingCollection(false);
        }
    };

    const handleDeleteCollection = (record: ICollection) => {
        setCollectionToDelete(record);
        setIsDeleteModalVisible(true);
    };

    const handleCloseDeleteModal = () => {
        setIsDeleteModalVisible(false);
        setCollectionToDelete(null);
    };

    const handleConfirmDelete = async () => {
        if (!collectionToDelete?.id) {
            return;
        }
        setDeleteLoading(true);
        try {
            const res: any = await dispatch(request({
                url: updateOrDeleteCollectionApi,
                method: 'DELETE',
                data: {
                    companyID: info.id,
                    paymentCycle: selectedPaymentCycle,
                    id: collectionToDelete.id,
                }
            }));
            if (res?.data?.status === 200) {
                message.success(res?.data?.message);
                if (selectedPaymentCycle !== null) {
                    const Collections = await getCollections(selectedPaymentCycle);
                    setCollections(Collections);
                    setInvoiceSummary(res?.data?.data?.invoiceSummary || {});
                }
                handleCloseDeleteModal();
            } else {
                message.error(res?.data?.message || errorMessage, 4);
                handleCloseDeleteModal();
            }
        } catch (error: any) {
            message.error(errorMessage, 4);
            handleCloseDeleteModal();
        }
        setDeleteLoading(false);
    };

    const columns = [
        {
            title: 'Collection Date',
            dataIndex: 'collectionDate',
            key: 'collectionDate',
            width: 160,
            render: (text: string) => (
                <span style={{ fontWeight: 500, color: '#1890ff' }}>
                    {helper.formatDate(text, 'DD/MM/YYYY')}
                </span>
            )
        },
        {
            title: 'Company Name',
            dataIndex: 'companyName',
            key: 'companyName',
            width: 220,
            render: (text: string) => (
                <span style={{ color: '#262626' }}>{text || 'N/A'}</span>
            )
        },
        {
            title: 'Serial Number',
            dataIndex: 'serialNumber',
            key: 'serialNumber',
            width: 200,
            render: (text: string) => (
                <span style={{ fontFamily: 'monospace', color: '#595959' }}>
                    {text || 'N/A'}
                </span>
            )
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            width: 180,
            align: 'right' as const,
            render: (amount: number) => (
                <span style={{
                    fontWeight: 600,
                    color: '#52c41a',
                    fontSize: '15px'
                }}>
                    {helper.amountFormatter(amount)} EGP
                </span>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 200,
            align: 'center' as const,
            fixed: 'right' as const,
            render: (_: any, record: ICollection) => (
                <div style={{
                    display: 'flex',
                    gap: '8px',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                    <Button
                        type="primary"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEditCollection(record)}
                        style={{
                            borderRadius: '6px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '0 12px',
                            fontSize: '13px',
                            fontWeight: 500,
                            boxShadow: '0 2px 4px rgba(24, 144, 255, 0.2)'
                        }}
                    >
                        Edit
                    </Button>
                    <Button
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteCollection(record)}
                        style={{
                            borderRadius: '6px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '0 12px',
                            fontSize: '13px',
                            fontWeight: 500,
                            boxShadow: '0 2px 4px rgba(255, 77, 79, 0.2)'
                        }}
                    >
                        Delete
                    </Button>
                </div>
            )
        }
    ];

    if (!info?.id) {
        return (
            <div style={{
                textAlign: 'center',
                padding: '80px 20px',
                backgroundColor: '#fafafa',
                borderRadius: '12px',
                border: '2px dashed #e8e8e8'
            }}>
                <CalendarOutlined style={{ fontSize: '48px', color: '#bfbfbf', marginBottom: '16px' }} />
                <p style={{
                    fontSize: '16px',
                    color: '#8c8c8c',
                    margin: 0,
                    fontWeight: 500
                }}>
                    Please select a company first
                </p>
            </div>
        );
    }

    return (
        <div style={{
            padding: '32px',
            minHeight: '100vh'
        }}>
            <div style={{
                backgroundColor: '#fff',
                padding: '32px',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                maxWidth: '1400px',
                margin: '0 auto'
            }}>
                {/* Header Section */}
                <div style={{
                    marginBottom: '32px',
                    paddingBottom: '20px',
                    borderBottom: '2px solid #f0f0f0'
                }}>
                    <h2 style={{
                        margin: 0,
                        fontSize: '24px',
                        fontWeight: 600,
                        color: '#262626',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                    }}>
                        <CalendarOutlined style={{ color: '#1890ff' }} />
                        Edit and Delete Collections
                    </h2>
                    <p style={{
                        margin: '8px 0 0 0',
                        fontSize: '14px',
                        color: '#8c8c8c'
                    }}>
                        Select an invoice month to view and manage collections
                    </p>
                </div>

                {/* Filter Section */}
                <Row gutter={[24, 24]} style={{ marginBottom: '32px' }}>
                    <Col xs={24} sm={24} md={12} lg={8} xl={8}>
                        <div style={{ marginBottom: '8px' }}>
                            <label
                                htmlFor="invoiceMonth"
                                style={{
                                    display: 'block',
                                    marginBottom: '10px',
                                    fontWeight: 600,
                                    fontSize: '15px',
                                    color: '#262626',
                                    letterSpacing: '0.3px'
                                }}
                            >
                                Invoice Month <span style={{ color: '#ff4d4f', marginLeft: '4px' }}>*</span>
                            </label>
                        </div>
                        <Select
                            id="invoiceMonth"
                            placeholder={loadingPaymentCycles ? "Loading months..." : "Select an invoice month"}
                            style={{
                                width: '100%',
                                height: '42px'
                            }}
                            value={selectedPaymentCycle}
                            onChange={handlePaymentCycleChange}
                            allowClear
                            loading={loadingPaymentCycles}
                            size="large"
                            suffixIcon={loadingPaymentCycles ? undefined : <CalendarOutlined style={{ color: '#1890ff' }} />}
                            disabled={loadingPaymentCycles}
                            notFoundContent={loadingPaymentCycles ? <Spin size="small" /> : 'No months available'}
                            optionLabelProp="label"
                            dropdownStyle={{
                                borderRadius: '8px',
                                padding: '8px',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                            }}
                            dropdownRender={(menu) => (
                                <div style={{ padding: '4px' }}>
                                    {menu}
                                </div>
                            )}
                        >
                            {Array.isArray(paymentCycles) &&
                                paymentCycles?.map((paymentCycle: {
                                    id: number;
                                    paymentCycle: number;
                                    cutOffCycleDate: string;
                                    isLocked?: boolean;
                                }) => {
                                    const formattedDate = moment(paymentCycle?.cutOffCycleDate)?.format('MMMM YYYY');
                                    const dayOfMonth = moment(paymentCycle?.cutOffCycleDate)?.format('DD');

                                    return (
                                        <Option
                                            key={paymentCycle.id}
                                            value={paymentCycle.paymentCycle}
                                            label={formattedDate}
                                        >
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                padding: '4px 0',
                                                width: '100%'
                                            }}>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '12px',
                                                    flex: 1
                                                }}>
                                                    <div style={{
                                                        width: '40px',
                                                        height: '40px',
                                                        borderRadius: '8px',
                                                        backgroundColor: '#e6f7ff',
                                                        border: '2px solid #91d5ff',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        flexShrink: 0
                                                    }}>
                                                        <span style={{
                                                            fontSize: '12px',
                                                            fontWeight: 700,
                                                            color: '#1890ff',
                                                            lineHeight: 1
                                                        }}>
                                                            {dayOfMonth}
                                                        </span>
                                                        <span style={{
                                                            fontSize: '9px',
                                                            color: '#69c0ff',
                                                            fontWeight: 500,
                                                            textTransform: 'uppercase',
                                                            lineHeight: 1,
                                                            marginTop: '2px'
                                                        }}>
                                                            {moment(paymentCycle?.cutOffCycleDate).format('MMM')}
                                                        </span>
                                                    </div>
                                                    <div style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: '2px',
                                                        flex: 1
                                                    }}>
                                                        <span style={{
                                                            fontSize: '15px',
                                                            fontWeight: 600,
                                                            color: '#262626',
                                                            lineHeight: 1.2
                                                        }}>
                                                            {formattedDate}
                                                        </span>
                                                        <span style={{
                                                            fontSize: '12px',
                                                            color: '#8c8c8c',
                                                            fontWeight: 400
                                                        }}>
                                                            Payment Cycle #{paymentCycle?.paymentCycle}
                                                        </span>
                                                    </div>
                                                </div>
                                                {paymentCycle.isLocked && (
                                                    <Tag
                                                        icon={<LockOutlined />}
                                                        color="error"
                                                        style={{
                                                            margin: 0,
                                                            borderRadius: '4px',
                                                            fontSize: '11px',
                                                            padding: '2px 8px',
                                                            height: '24px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '4px'
                                                        }}
                                                    >
                                                        Locked
                                                    </Tag>
                                                )}
                                            </div>
                                        </Option>
                                    );
                                })}
                        </Select>
                        {loadingPaymentCycles && (
                            <div style={{
                                marginTop: '8px',
                                fontSize: '12px',
                                color: '#8c8c8c',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}>
                                <Spin size="small" />
                                <span>Loading payment cycles...</span>
                            </div>
                        )}
                    </Col>
                </Row>

                {/* Summary Section */}
                {selectedPaymentCycle ? (
                    <Spin spinning={loadingCollections || updatingCollection || deleteLoading}>
                        <div style={{
                            marginTop: '24px',
                            marginBottom: '24px',
                            padding: '20px',
                            backgroundColor: '#fafafa',
                            borderRadius: '8px',
                            border: '1px solid #e8e8e8'
                        }}>
                            <Descriptions
                                title="Invoice Summary"
                                bordered
                                column={{ xxl: 2, xl: 2, lg: 2, md: 2, sm: 1, xs: 1 }}
                                size="small"
                            >
                                <Descriptions.Item label="Total Collections Amount" span={1}>
                                    <span style={{
                                        fontSize: '16px',
                                        fontWeight: 600,
                                        color: '#52c41a'
                                    }}>
                                        {helper.amountFormatter(invoiceSummary?.totalCollectionsAmount || 0)} EGP
                                    </span>
                                </Descriptions.Item>
                                <Descriptions.Item label="Invoice Total Amount" span={1}>
                                    <span style={{
                                        fontSize: '16px',
                                        fontWeight: 600,
                                        color: '#1890ff'
                                    }}>
                                        {helper.amountFormatter(invoiceSummary?.totalInvoiceAmount || 0)} EGP
                                    </span>
                                </Descriptions.Item>
                            </Descriptions>
                        </div>
                    </Spin>
                ) : (
                    <div
                        style={{
                            marginTop: '24px',
                            padding: '20px',
                            backgroundColor: '#fafafa',
                            borderRadius: '8px',
                            border: '2px dashed #e8e8e8',
                            textAlign: 'center',
                            fontSize: '16px',
                            fontWeight: 500
                        }}
                    >
                        <p style={{ margin: 0, color: '#8c8c8c', }}>Please select an invoice month to view total collections amount and invoice total amount</p>
                    </div>
                )}

                {/* Table Section */}
                {selectedPaymentCycle && (
                    <>
                        <div style={{
                            marginTop: '24px',
                            border: '1px solid #e8e8e8',
                            borderRadius: '8px',
                            overflow: 'hidden'
                        }}>
                            {collections?.length === 0 && !loadingCollections ? (
                                <div style={{
                                    padding: '80px 20px',
                                    backgroundColor: '#fafafa'
                                }}>
                                    <Empty
                                        description={
                                            <span style={{
                                                fontSize: '15px',
                                                color: '#8c8c8c',
                                                fontWeight: 500
                                            }}>
                                                No collections found for this invoice
                                            </span>
                                        }
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    />
                                </div>
                            ) : (
                                <Table
                                    columns={columns}
                                    dataSource={collections?.map((item, index) => ({ ...item, key: item?.id || index }))}
                                    bordered={false}
                                    loading={loadingCollections || updatingCollection || deleteLoading}
                                    pagination={{
                                        current: paginationConfig.current,
                                        pageSize: paginationConfig.pageSize,
                                        showSizeChanger: true,
                                        showTotal: (total, range) =>
                                            `${range[0]}-${range[1]} of ${total} collections`,
                                        pageSizeOptions: ['10', '20', '50', '100'],
                                        onChange: (page, pageSize) => {
                                            setPaginationConfig({
                                                current: page,
                                                pageSize: pageSize || 10,
                                            });
                                        },
                                        onShowSizeChange: (current, size) => {
                                            setPaginationConfig({
                                                current: 1,
                                                pageSize: size,
                                            });
                                        },
                                        style: {
                                            padding: '16px',
                                            backgroundColor: '#fafafa'
                                        }
                                    }}
                                    style={{
                                        backgroundColor: '#fff'
                                    }}
                                    onRow={(record) => ({
                                        style: {
                                            transition: 'all 0.2s',
                                            cursor: 'default'
                                        },
                                        onMouseEnter: (e) => {
                                            e.currentTarget.style.backgroundColor = '#f5f5f5';
                                        },
                                        onMouseLeave: (e) => {
                                            e.currentTarget.style.backgroundColor = '#fff';
                                        }
                                    })}
                                />
                            )}
                        </div>
                    </>
                )}

                {/* Empty State */}
                {!selectedPaymentCycle && (
                    <div style={{
                        textAlign: 'center',
                        padding: '80px 20px',
                        backgroundColor: '#fafafa',
                        borderRadius: '8px',
                        border: '2px dashed #e8e8e8',
                        marginTop: '24px'
                    }}>
                        <CalendarOutlined style={{
                            fontSize: '56px',
                            color: '#d9d9d9',
                            marginBottom: '20px'
                        }} />
                        <p style={{
                            fontSize: '16px',
                            color: '#8c8c8c',
                            margin: 0,
                            fontWeight: 500
                        }}>
                            Please select an invoice month to view collections
                        </p>
                    </div>
                )}
            </div>
            <EditCollectionModal
                visible={isEditModalVisible}
                collectionData={selectedCollection || undefined}
                onCancel={handleCloseEditModal}
                onUpdate={handleUpdateCollection}
            />
            <DeleteCollectionModal
                visible={isDeleteModalVisible}
                onCancel={handleCloseDeleteModal}
                onConfirm={handleConfirmDelete}
                loading={deleteLoading}
            />
        </div>
    );
};

export default EditAndDelete;
